/**
 * OAuth callback handling on web app boot.
 *
 * The auth server redirects back to the site root with the result in the URL
 * fragment (responseMode: 'fragment'). On startup we (1) normalize the loopback
 * host so IndexedDB PKCE state is reachable in dev, and (2) if the URL carries
 * callback params, finish the exchange and adopt the resulting OAuthSession as
 * the active account.
 *
 * Web-only.
 */

import {logger} from '#/logger'
import {oauthInit} from './oauth-web-client'

/**
 * atproto's loopback client redirects to the `127.0.0.1` form, but dev servers
 * commonly open `localhost`. IndexedDB (where PKCE/session state lives) is
 * per-origin, so a `localhost` start + `127.0.0.1` return would lose the state.
 * Rewrite `localhost` -> `127.0.0.1` early (before any OAuth call). No-op in prod.
 */
export function maybeRedirectLoopbackHost(): void {
  if (typeof window === 'undefined') return
  if (window.location.hostname === 'localhost') {
    const url = new URL(window.location.href)
    url.hostname = '127.0.0.1'
    window.location.replace(url.href)
  }
}

/**
 * If the current URL is an OAuth callback, complete it and hand the session to
 * `login`. Returns true if a callback was handled (caller should skip the normal
 * resume path). Strips the callback params from the URL on success.
 *
 * @param loginWithOAuthSession - session API method to adopt the OAuthSession.
 */
export async function tryFinishWebOAuthSignIn(
  loginWithOAuthSession: (
    oauthSession: unknown,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    logContext: any,
  ) => Promise<void>,
): Promise<boolean> {
  let result: Awaited<ReturnType<typeof oauthInit>>
  try {
    result = await oauthInit()
  } catch (e) {
    logger.error('oauth: callback init failed', {
      safeMessage: e instanceof Error ? e.message : String(e),
    })
    return false
  }

  if (!result) return false

  try {
    await loginWithOAuthSession(result.session, 'LoginForm')
  } finally {
    // Strip callback params (hash + query) from the URL regardless.
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.hash = ''
      url.search = ''
      window.history.replaceState(null, '', url.href)
    }
  }
  return true
}
